'use client';

import { twMerge } from '@shared/themes';
import dynamic from 'next/dynamic';
import { useEffect, type ComponentProps, type ReactNode } from 'react';
import { useLiveDraw } from '../../liveDraw/LiveDrawProvider.prod';
import type { LiveDrawSlot } from '../../liveDraw/resolveLiveDrawStage';
import { LiveDrawGridSkeleton } from '../../skeletons/LiveDrawGridSkeleton';
import { LiveDrawPromoSkeleton } from '../../skeletons/LiveDrawPromoSkeleton';
import { LIVE_DRAW_PANEL_CLASSES } from './liveDrawClasses';
import { ReplayDrawSlot } from './ReplayDrawSlot';

/** The waiting stages `LiveDrawStage` fills with the promotion rather than the board. */
const PROMO_STAGE_KEYS: ReadonlySet<LiveDrawStageKey> = new Set(['previous_result', 'selling_countdown']);

/**
 * Stands in for the board while its chunk loads. The stage is already known by then, so the
 * skeleton takes the shape the loaded card will: the promotion before the draw, the board after.
 */
function LiveDrawStageFallback() {
  const { stage, lotteryName } = useLiveDraw();

  if (PROMO_STAGE_KEYS.has(stage.slot.kind)) {
    return <LiveDrawPromoSkeleton lotteryName={lotteryName} />;
  }

  return <LiveDrawGridSkeleton lotteryName={lotteryName} />;
}

/**
 * The board and its Lottie animations are several hundred KB of JSON, and only this panel ever
 * shows them. Loading them lazily keeps them out of the `@shared/components` barrel's static graph,
 * so Lottoland UK's results pages — which never render a live draw — do not carry them.
 *
 * `ssr: false` because the whole subtree is animation- and clock-driven: dotLottie is browser-only,
 * and a server render of a time-dependent board would only mismatch on hydration.
 */
const LiveDrawStage = dynamic(() => import('./LiveDrawStage').then(mod => mod.LiveDrawStage), { ssr: false, loading: LiveDrawStageFallback });

/** Kept identical to the specifier above so both resolve to the same chunk. */
function importLiveDrawStage() {
  return import('./LiveDrawStage');
}

type LiveDrawStageKey = LiveDrawSlot['kind'];

type LiveDrawStages = Partial<Record<LiveDrawStageKey, ReactNode>>;

type LiveDrawPanelProps = ComponentProps<'div'> & {
  /** Replaces the placeholder for a given stage, so stage UI can land ticket by ticket. */
  stages?: LiveDrawStages;
};

/**
 * Live draw tab for lottery results pages.
 *
 * Sequencing lives on `useLiveDraw`. Your Numbers already matches against `revealedNumbers` from
 * that context; this panel will do the same once ball highlighting lands.
 * The “Replay draw” control sits under the stage (WEBD-949) and stays up through a replay's own
 * playback (WEBD-1528), easing open rather than pushing the page down when it appears.
 */
function LiveDrawPanel({ stages, className, ...props }: LiveDrawPanelProps) {
  const { stage, isReplay, canStartReplay, startReplay } = useLiveDraw();
  const stageKey = stage.slot.kind;

  // Warm the chunk as soon as the panel mounts rather than when the first board stage arrives.
  // The panel is on screen through the pre-draw stages, so by the time `draw_starting` lands the
  // chunk is already cached and the board appears without a gap. Playback is clock-synced, so a
  // slow load degrades to a late first frame rather than a desynced animation.
  useEffect(() => {
    void importLiveDrawStage();
  }, []);

  const override = stages?.[stageKey];

  return (
    <div
      data-testid="results-live-draw-panel"
      {...props}
      className={twMerge(LIVE_DRAW_PANEL_CLASSES, className)}
      data-track-container="Live Draw"
      data-live-draw-mode={isReplay ? 'replay' : 'live'}
    >
      {override ?? <LiveDrawStage />}
      <ReplayDrawSlot canStartReplay={canStartReplay} onReplay={startReplay} />
    </div>
  );
}

export { LiveDrawPanel };
export type { LiveDrawPanelProps, LiveDrawStageKey, LiveDrawStages };
